import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import type { Subscription } from '../types';
import { subscriptionService } from '../services/occupation/subscriptionService';
import { tokenService } from '../services/membership/tokenService'; // ADDED FOR TOKEN SYSTEM
import { pricingService } from '../services/membership/pricingService'; // ADDED FOR TOKEN SYSTEM

interface SubscriptionContextType {
  subscription: Subscription | null;
  setSubscription: (sub: Subscription | null) => void;
  loading: boolean;
  /** True once the user has an active/trialing subscription — gates career features. */
  hasAccess: boolean;
  refresh: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    const active = await subscriptionService.getActive();
    setSubscription(active);
    setLoading(false);
  };

  useEffect(() => {
    refresh();
  }, []);

  // ADDED FOR TOKEN SYSTEM: when the subscription becomes active/trialing on a
  // new plan, grant that tier's monthly token allocation exactly once per plan.
  const grantedPlanRef = useRef<string | null>(null);
  useEffect(() => {
    if (!subscription) return;
    const isActive = subscription.status === 'active' || subscription.status === 'trialing';
    // Token allocation is driven by the membership tier the plan maps to (plan
    // ids match tier ids), so each tier grants its own monthly token amount.
    const tokens = pricingService.getTierById(subscription.planId)?.monthlyTokens ?? 0;
    if (isActive && tokens && grantedPlanRef.current !== subscription.planId) {
      grantedPlanRef.current = subscription.planId;
      void tokenService.earn(tokens, 'subscription', `${subscription.planName} plan tokens`);
    }
  }, [subscription]);

  const hasAccess =
    !!subscription && (subscription.status === 'active' || subscription.status === 'trialing');

  return (
    <SubscriptionContext.Provider value={{ subscription, setSubscription, loading, hasAccess, refresh }}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscriptionContext() {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscriptionContext must be used within a SubscriptionProvider');
  }
  return context;
}
